import { useCallback, useState } from "react";

import { BlockBlobClient } from "@azure/storage-blob";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  createDocumentRequestApiV1CasesCaseIdDocumentRequestsPost,
  createReferrerCaseEndpointApiV1CasesReferrerIntakePost,
  refreshDocumentRequestApiV1CasesCaseIdDocumentRequestsRequestIdRefreshPost,
} from "@/api/generated/sdk.gen";
import type {
  ProviderDocumentRequestCreateSchema,
  ReferrerCaseCreateSchema,
} from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

import { fetchFileUploadSasUrl } from "@hooks/queries/use-files";

/** Classification folder the signed consent (ClientAuth) is stored under. */
export const CONSENT_CLASSIFICATION = "Consent";

export type UploadedConsent = {
  /** Blob path the backend stores as `consent_file_path`. */
  blobPath: string;
  filename: string;
};

/**
 * Upload the signed examinee consent (ClientAuth) directly to blob storage and
 * return its blob path. Unlike `useFileUpload` this does NOT create a
 * DocSorterFile row or trigger summarisation — consent is not a medical
 * document to classify; it's an attachment on the request.
 */
export const useConsentUpload = () => {
  const [isUploading, setIsUploading] = useState(false);

  const uploadConsent = useCallback(
    async ({
      file,
      caseNumber,
    }: {
      file: File;
      caseNumber: string;
    }): Promise<UploadedConsent> => {
      try {
        setIsUploading(true);
        const { url, blob_path } = await fetchFileUploadSasUrl({
          case_number: caseNumber,
          classification: CONSENT_CLASSIFICATION,
          filename: file.name,
        });
        await new BlockBlobClient(url).uploadData(file);
        return { blobPath: blob_path, filename: file.name };
      } finally {
        setIsUploading(false);
      }
    },
    []
  );

  return { uploadConsent, isUploading };
};

/**
 * Raise a document request to a provider for a case. Backend enforces
 * consent-required + only-once (409) — surface those to the caller.
 */
export const useCreateDocumentRequest = (caseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: ProviderDocumentRequestCreateSchema) => {
      const response =
        await createDocumentRequestApiV1CasesCaseIdDocumentRequestsPost({
          client: apiClient,
          path: { case_id: caseId },
          body,
        });
      if ("error" in response && response.error) {
        throw new Error(
          (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.DOCUMENT_REQUESTS, caseId],
      });
    },
  });
};

/**
 * Check for updates on a document request (demo poll). Advances the simulated
 * trickle server-side; received documents land on the case as files, so refresh
 * both the request list and the case files list.
 */
export const useRefreshDocumentRequest = (caseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const response =
        await refreshDocumentRequestApiV1CasesCaseIdDocumentRequestsRequestIdRefreshPost(
          {
            client: apiClient,
            path: { case_id: caseId, request_id: requestId },
          }
        );
      if ("error" in response && response.error) {
        throw new Error(
          (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.DOCUMENT_REQUESTS, caseId],
      });
      // Newly received documents are ingested as case files → refresh them so
      // they appear in Assessment Prep.
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.FILES] });
    },
  });
};

/** Referrer-initiated lightweight case creation (no report template). */
export const useCreateReferrerCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: ReferrerCaseCreateSchema) => {
      const response = await createReferrerCaseEndpointApiV1CasesReferrerIntakePost({
        client: apiClient,
        body,
      });
      if ("error" in response && response.error) {
        throw new Error(
          (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.CASES] });
    },
  });
};
